import { computed, inject, Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { StorageService } from './storage.service';
import { CurrentUser, LoginRequest, TokenResponse, UserType } from '../models/auth.models';

const STORAGE_KEYS = {
  access: 'access_token',
  refresh: 'refresh_token',
  expiresAt: 'expires_at',
} as const;

/**
 * Email/password JWT auth against the Idio backend (prefix /api/v1/auth).
 *
 * - Access token (short-lived JWT) + refresh token (opaque, rotated on refresh)
 *   are persisted in localStorage via {@link StorageService}.
 * - Identity (email/name/type/role flags) is derived by decoding the access
 *   token's JWT payload — see {@link decodeUser}.
 * - The HTTP interceptor drives single-flight refresh-on-401 using {@link refresh}.
 *
 * See the `idio-backend-api` skill for the full contract.
 */
@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly storage = inject(StorageService);

  private readonly apiBase = `${import.meta.env['NG_APP_API_URL'] ?? ''}/api/v1`;

  /** Current authenticated identity, or null when logged out. */
  readonly currentUser = signal<CurrentUser | null>(this.decodeUser(this.storage.getItem(STORAGE_KEYS.access)));

  /** True for users who may reach admin-or-root endpoints (admin OR root). */
  readonly canAdmin = computed(() => {
    const u = this.currentUser();
    return !!u && (u.is_admin || u.is_root);
  });

  // ── Auth flows ──────────────────────────────────────────────────────────────

  login(credentials: LoginRequest): Observable<void> {
    return this.http.post<TokenResponse>(`${this.apiBase}/auth/login`, credentials).pipe(
      tap(res => this.storeTokens(res)),
      map(() => void 0),
    );
  }

  /**
   * Exchange the stored refresh token for a fresh token pair (rotation).
   * Errors if no refresh token is stored. Used by the interceptor on 401.
   */
  refresh(): Observable<TokenResponse> {
    const refreshToken = this.storage.getItem(STORAGE_KEYS.refresh);
    if (!refreshToken) {
      return throwError(() => new Error('No refresh token'));
    }
    return this.http
      .post<TokenResponse>(`${this.apiBase}/auth/refresh`, { refresh_token: refreshToken })
      .pipe(tap(res => this.storeTokens(res)));
  }

  /**
   * Best-effort server-side revocation of the refresh token, then clear local
   * session. Fire-and-forget so logout always completes locally.
   */
  logout(): void {
    const refreshToken = this.storage.getItem(STORAGE_KEYS.refresh);
    if (refreshToken) {
      this.http
        .post(`${this.apiBase}/auth/logout`, { refresh_token: refreshToken })
        .subscribe({ error: () => {} });
    }
    this.clearSession();
  }

  /** Clear all local auth state without contacting the server. */
  clearSession(): void {
    this.storage.removeItem(STORAGE_KEYS.access);
    this.storage.removeItem(STORAGE_KEYS.refresh);
    this.storage.removeItem(STORAGE_KEYS.expiresAt);
    this.currentUser.set(null);
  }

  // ── Auth state ──────────────────────────────────────────────────────────────

  /** True while the stored access token exists and has not expired. */
  isLoggedIn(): boolean {
    const token = this.storage.getItem(STORAGE_KEYS.access);
    const expiresAt = this.storage.getItem(STORAGE_KEYS.expiresAt);
    if (!token || !expiresAt) return false;
    return this.nowSeconds() < parseInt(expiresAt, 10);
  }

  /**
   * Resolve whether the user is authenticated, transparently refreshing an
   * expired access token when a refresh token is still available. Used by
   * authGuard so a stale access token doesn't bounce the user to /login.
   */
  ensureAuthenticated(): Observable<boolean> {
    if (this.isLoggedIn()) return of(true);
    if (!this.storage.getItem(STORAGE_KEYS.refresh)) return of(false);
    return this.refresh().pipe(
      map(() => true),
      catchError(() => {
        this.clearSession();
        return of(false);
      }),
    );
  }

  getAccessToken(): string | null {
    return this.storage.getItem(STORAGE_KEYS.access);
  }

  // ── Identity accessors ────────────────────────────────────────────────────────

  get userEmail(): string | null {
    return this.currentUser()?.email ?? null;
  }

  get userName(): string | null {
    return this.currentUser()?.name ?? null;
  }

  get userType(): UserType | null {
    return this.currentUser()?.type ?? null;
  }

  // ── Internals ──────────────────────────────────────────────────────────────

  private storeTokens(res: TokenResponse): void {
    this.storage.setItem(STORAGE_KEYS.access, res.access_token);
    this.storage.setItem(STORAGE_KEYS.refresh, res.refresh_token);
    this.storage.setItem(STORAGE_KEYS.expiresAt, String(this.nowSeconds() + res.expires_in));
    this.currentUser.set(this.decodeUser(res.access_token));
  }

  /** Decode identity from a JWT access token's payload. Returns null if absent/malformed. */
  private decodeUser(token: string | null): CurrentUser | null {
    if (!token) return null;
    try {
      const payload = JSON.parse(
        atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')),
      );
      const type: UserType | null = payload['type'] ?? null;
      return {
        user_id: payload['sub'] ?? '',
        email: payload['email'] ?? null,
        name: payload['name'] ?? null,
        type,
        is_admin: type === 'admin',
        is_root: type === 'root',
      };
    } catch {
      return null;
    }
  }

  private nowSeconds(): number {
    return Math.floor(Date.now() / 1000);
  }
}
